import React from 'react';
import { ActionsDropdown, Badge, Card, CardBody, CardHeader } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import PaymentDashboard from '../../../Components/PaymentDashboard';
import type * as survey from '@aidkitorg/types/lib/survey';

interface PaymentDashboardCardProps {
  /** The PaymentDashboard survey component configuration */
  component: survey.PaymentDashboard;
  /** Pre-computed payment results */
  results: any[];
  /** Title to display in the card header */
  title?: string;
  /** Static widget ID from the dashboard widget library */
  staticWidgetId?: string;
  /** When true, shows delete action in card header */
  isEditMode?: boolean;
  /** Callback to delete this component from the dashboard */
  onDelete?: () => void;
}

/**
 * Renders a PaymentDashboard inside a Card with standard dashboard header
 * (title, static widget badge, edit-mode delete action).
 */
export function PaymentDashboardCard({
  component,
  results,
  title,
  staticWidgetId,
  isEditMode,
  onDelete,
}: PaymentDashboardCardProps) {
  const L = useLocalizedStrings();
  const displayTitle = title || 'Payment Dashboard';

  return (
    <Card className="rounded-md w-full h-full">
      <CardHeader
        title={
          <div className="flex flex-wrap items-center gap-2 text-lg font-semibold px-4 py-2">
            {displayTitle}
            {staticWidgetId && <Badge variant="neutral">{L.admin_panel.dashboards.static_widget}</Badge>}
          </div>
        }
        actionButton={isEditMode && onDelete ? (
          <ActionsDropdown
            actions={[{
              label: L.admin_panel.dashboards.delete,
              onClick: onDelete,
              icon: 'TrashIcon',
              variant: 'warn' as const,
            }]}
            item={component}
          />
        ) : undefined}
      />
      <CardBody className="flex-1 overflow-auto">
        <PaymentDashboard
          component={component}
          results={results}
        />
      </CardBody>
    </Card>
  );
}
